import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Users, Hammer, Settings, History, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  verified: 'bg-green-100 text-green-700',
  rewarded: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  duplicate: 'bg-neutral-200 text-neutral-600',
  ineligible: 'bg-neutral-200 text-neutral-600',
};

export default function AdminReferrals() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('contractors');

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/referral-program/overview`);
      setData(res.data);
      setSettings(res.data.settings);
    } catch (e) {
      toast.error('Failed to load referral data');
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const decide = async (id, action) => {
    try {
      const res = await axios.put(`${API}/admin/referral-program/contractor-referrals/${id}`, { action });
      toast.success(res.data.message);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const saveSettings = async () => {
    try {
      const numeric = ['homeowner_referrals_per_entry', 'entries_per_milestone', 'max_homeowner_entries', 'contractor_entries_awarded', 'max_rewarded_contractor_referrals'];
      const payload = { ...settings };
      numeric.forEach((k) => { payload[k] = Number(payload[k]); });
      await axios.put(`${API}/admin/referral-program/settings`, payload);
      toast.success('Referral settings saved');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  if (!data) return <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-referrals-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-neutral-900">Referral Management</h1>
        <Button variant="outline" size="sm" onClick={fetchAll} data-testid="admin-ref-refresh"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['contractors', 'Contractor Referrals', Hammer], ['homeowners', 'Homeowner Referrals', Users], ['settings', 'Reward Settings', Settings], ['audit', 'Audit History', History]].map(([key, label, Icon]) => (
          <button key={key} data-testid={`admin-ref-tab-${key}`} onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${tab === key ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'contractors' && (
        <div className="space-y-3">
          {data.top_recommended_contractors.length > 0 && (
            <div className="bg-white rounded-xl border border-neutral-200 p-4 text-sm">
              <span className="font-semibold text-neutral-800 mr-2">Most recommended:</span>
              {data.top_recommended_contractors.slice(0, 5).map((c) => (
                <span key={c.name} className="inline-block mr-2 px-2 py-0.5 bg-neutral-100 rounded-full text-xs capitalize">{c.name} ({c.count})</span>
              ))}
            </div>
          )}
          {data.contractor_referrals.length === 0 && <p className="text-neutral-400 text-sm">No contractor referrals submitted yet.</p>}
          {data.contractor_referrals.map((c) => (
            <div key={c.contractor_referral_id} data-testid={`admin-contractor-ref-${c.contractor_referral_id}`} className="bg-white rounded-xl border border-neutral-200 p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-neutral-900">{c.contractor_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[c.contractor_verification_status] || 'bg-neutral-100'}`}>{c.contractor_verification_status}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[c.reward_status] || 'bg-neutral-100'}`}>reward: {c.reward_status}</span>
                  </div>
                  <p className="text-sm text-neutral-500 mt-1">{c.service_category} · {c.city} · {c.contractor_email} · {c.contractor_phone}</p>
                  <p className="text-sm text-neutral-600 mt-1">"{c.recommendation_reason}"</p>
                  <p className="text-xs text-neutral-400 mt-1">Work: {c.work_description} ({c.approximate_service_date}) · Relationship: {c.homeowner_relationship}</p>
                </div>
                {c.contractor_verification_status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" data-testid={`approve-${c.contractor_referral_id}`} onClick={() => decide(c.contractor_referral_id, 'approve')} className="bg-green-600 hover:bg-green-700 text-white rounded-xl">Approve</Button>
                    <Button size="sm" variant="outline" data-testid={`reject-${c.contractor_referral_id}`} onClick={() => decide(c.contractor_referral_id, 'reject')} className="rounded-xl text-red-600 border-red-200">Reject</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'homeowners' && (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase">
              <tr>{['Referrer', 'Clicks', 'Signups', 'Verified', 'Code'].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {data.homeowner_referrals.map((r) => (
                <tr key={r.referral_id}>
                  <td className="px-4 py-3"><span className="font-medium text-neutral-900">{r.referrer_name}</span><br /><span className="text-xs text-neutral-400">{r.referrer_email}</span></td>
                  <td className="px-4 py-3">{r.clicks || 0}</td>
                  <td className="px-4 py-3">{r.signups || 0}</td>
                  <td className="px-4 py-3 font-semibold">{r.verified_referrals}</td>
                  <td className="px-4 py-3 text-xs text-neutral-400">{r.referral_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'settings' && settings && (
        <div className="bg-white rounded-xl border border-neutral-200 p-6 max-w-xl space-y-4">
          {[
            ['homeowner_referrals_per_entry', 'Verified homeowner referrals required per additional entry'],
            ['entries_per_milestone', 'Entries awarded per homeowner milestone'],
            ['max_homeowner_entries', 'Maximum homeowner referral entries (per giveaway)'],
            ['contractor_entries_awarded', 'Contractor referral entries awarded'],
            ['max_rewarded_contractor_referrals', 'Maximum rewarded contractor referrals'],
          ].map(([key, label]) => (
            <div key={key}>
              <Label className="text-sm">{label}</Label>
              <Input data-testid={`setting-${key}`} type="number" className="mt-1 rounded-xl" value={settings[key]} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} />
            </div>
          ))}
          {[['leaderboard_enabled', 'Public leaderboard enabled'], ['contractor_referrals_enabled', 'Contractor referrals enabled'], ['sharing_buttons_enabled', 'Referral sharing buttons enabled']].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between">
              <Label className="text-sm">{label}</Label>
              <Switch data-testid={`setting-${key}`} checked={!!settings[key]} onCheckedChange={(v) => setSettings({ ...settings, [key]: v })} />
            </div>
          ))}
          <Button data-testid="save-referral-settings" onClick={saveSettings} className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">Save Settings</Button>
        </div>
      )}

      {tab === 'audit' && (
        <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100">
          {data.audit_history.length === 0 && <p className="p-5 text-sm text-neutral-400">No referral audit events yet.</p>}
          {data.audit_history.map((a) => (
            <div key={a.audit_id} className="px-5 py-3 text-sm flex justify-between flex-wrap gap-2">
              <span><span className="font-medium text-neutral-900 capitalize">{a.action.replace(/_/g, ' ')}</span> — {a.detail}</span>
              <span className="text-xs text-neutral-400">{new Date(a.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
